"use client";


import { CART_COOKIE } from "@/config/cart";
import CheckoutSkeleton from "@app/_components/Checkout/CheckoutSkeleton";
import { useMarket, useSiteInfo } from "@app/_context";
import useEvents from "@app/_hooks/useEvents";
import { deleteCookie, hasCookie } from "cookies-next";
import { useEffect } from "react";
import { useCheckout, useCheckoutMethod } from "./Checkout.hooks";
import { checkoutStore } from "./Checkout.store";
import CheckoutAddressForm from "./CheckoutAddressForm";
import CheckoutContainer from "./CheckoutContainer";
import CheckoutDelivery from "./CheckoutDelivery";
import CheckoutEmpty from "./CheckoutEmpty";
import CheckoutHeading from "./CheckoutHeading";
import CheckoutItems from "./CheckoutItems";
import { CheckoutMadeToOrderLabel } from "./CheckoutMadeToOrderLabel";
import CheckoutMinimumOrderMessage from "./CheckoutMinimumOrderMessage";
import CheckoutOffers from "./CheckoutOffers";
import CheckoutPayment from "./CheckoutPayment";
import CheckoutSummary from "./CheckoutSummary";

export default function Checkout() {
  const { data, refetch, error, isFetchedAfterMount } = useCheckout();
  const deliveryOption = checkoutStore((state) => state.deliveryOption);
  const marketConfig = useMarket().state.market;
  const { minimumOrderValue } = useSiteInfo();
  const { beginCheckout } = useEvents();

  const setAbleToComplete = checkoutStore((state) => state.setAbleToComplete);

  useCheckoutMethod();

  useEffect(() => {
    if (data) {
      beginCheckout(data);
    }
  }, [data, beginCheckout]);

  useEffect(() => {
    refetch();
  }, [refetch, deliveryOption]);

  useEffect(() => {
    if (
      error?.message.includes("Invalid cart") ||
      error?.message.includes("Not Found")
    ) {
      if (hasCookie(CART_COOKIE)) {
        deleteCookie(CART_COOKIE);
        refetch();
      }
    }
  }, [error, refetch]);

  useEffect(() => {
    if (!data?.items) {
      return;
    }

    const someCartItemIsSoldOut = data.items.some(
      (item) => item.stockWatchStatus?.ableToComplete === false,
    );

    if (someCartItemIsSoldOut) {
      setAbleToComplete(false);
      return;
    }

    if (data.totalGrossAmount === undefined) {
      setAbleToComplete(true);
      return;
    }

    const minOrderValueThreshold =
      minimumOrderValue ?? marketConfig.minimumOrderValue ?? 0;

    const belowMinimumOrderValue =
      data.totalGrossAmount - data.totalFreightInclVat < minOrderValueThreshold;

    setAbleToComplete(
      !belowMinimumOrderValue || data.purchasableBelowMinimumOrderValue,
    );
  }, [
    data?.items,
    data?.totalGrossAmount,
    data?.totalFreightInclVat,
    data?.purchasableBelowMinimumOrderValue,
    marketConfig,
    minimumOrderValue,
    setAbleToComplete,
  ]);

  if (!isFetchedAfterMount) {
    return (
      <CheckoutContainer>
        <CheckoutSkeleton />
      </CheckoutContainer>
    );
  }

  if (isFetchedAfterMount && !data?.items.length) {
    return (
      <CheckoutContainer>
        <CheckoutEmpty />
      </CheckoutContainer>
    );
  }

  return (
    <CheckoutContainer>
      <CheckoutHeading />
      <CheckoutMinimumOrderMessage />
      <CheckoutItems />
      <CheckoutMadeToOrderLabel />
      <CheckoutSummary />
      <CheckoutOffers />
      <CheckoutAddressForm />
      <CheckoutDelivery />
      <CheckoutPayment />
    </CheckoutContainer>
  );
}
